import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { Pill } from '../../../shared/Pill';
import Accordion from '../../../shared/Accordion';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { formatDateTime } from '../../../shared/tenantClock';
import { askConfirm } from '../../../shared/AppDialogs';

const STATUS_PILLS = {
  connected: { label: 'Connected', color: '#1a7f4b', background: 'rgba(26, 127, 75, 0.12)' },
  needs_reconnect: { label: 'Needs reconnect', color: '#b91c1c', background: 'rgba(185, 28, 28, 0.1)' },
  unreachable: { label: 'Connected', color: '#1a7f4b', background: 'rgba(26, 127, 75, 0.12)' },
  not_connected: { label: 'Not connected', color: '#5a6b85', background: '#dde3ea' },
};

const PRIMARY_BUTTON = { padding: '7px 14px', borderRadius: 6, border: 'none', background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 };
const SECONDARY_BUTTON = { padding: '7px 14px', borderRadius: 6, border: '1px solid #bfc8d6', background: '#fff', color: '#333', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 };

function Detail({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, fontSize: 14, marginTop: 6 }}>
      <span style={{ width: 110, color: '#666' }}>{label}</span>
      <span>{children}</span>
    </div>
  );
}

// Platform > API Integrations' QuickBooks card (issue #129) — Admin only.
// Connect sends the browser to Intuit to sign in and approve access;
// Intuit sends it back to the backend's callback, which returns here with
// ?quickbooks=connected (or =error plus a message) for this card to show.
// The connection is checked live with QuickBooks every time the card
// loads, so a revoked one shows Needs reconnect straight away.
function QuickBooksCard() {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [returned, setReturned] = useState(() => {
    const params = new URLSearchParams(location.search);
    const outcome = params.get('quickbooks');
    return outcome ? { ok: outcome === 'connected', message: params.get('quickbooks_message') } : null;
  });

  useEffect(() => {
    // Drop the outcome from the address bar so a refresh doesn't repeat it.
    if (returned) navigate(location.pathname, { replace: true });
    axios.get('/api/quickbooks')
      .then((res) => setStatus(res.data.data))
      .catch((err) => showError(err, 'Failed to load the QuickBooks connection'));
    // eslint-disable-next-line
  }, []);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const res = await axios.post('/api/quickbooks/connect', { return_to: window.location.href });
      window.location.assign(res.data.data.url);
    } catch (err) {
      showError(err, 'Couldn’t start the QuickBooks sign-in');
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!(await askConfirm(`Disconnect QuickBooks${status?.company_name ? ` (${status.company_name})` : ''}?\n\nOptim will stop reading from and sending to QuickBooks until it's connected again. Optim is also removed from the company's connected apps in QuickBooks.`, { confirmLabel: 'Disconnect' }))) return;
    setBusy(true);
    try {
      const res = await axios.delete('/api/quickbooks');
      setStatus(res.data.data);
      setReturned(null);
      dismissError();
    } catch (err) {
      showError(err, 'Disconnect failed');
    }
    setBusy(false);
  };

  const state = status?.status || 'not_connected';
  const pill = STATUS_PILLS[state];
  const connected = state !== 'not_connected';

  const title = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      QuickBooks Online
      {status && <Pill color={pill.color} background={pill.background}>{pill.label}</Pill>}
    </span>
  );

  return (
    <Accordion title={title} defaultOpen={!!returned}>
      <div style={{ fontSize: 13, color: '#666', marginTop: -6, marginBottom: 12, maxWidth: 480 }}>
        Connects Optim to the company’s QuickBooks Online file so Optim can read from and send to QuickBooks. Only an Admin can see or change this connection.
      </div>

      {returned && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 12 }}>
          {returned.ok ? <CheckCircle2 size={15} color="#1a7f4b" /> : <XCircle size={15} color="#b91c1c" />}
          <span style={{ color: returned.ok ? '#1a7f4b' : '#b91c1c' }}>
            {returned.ok ? 'QuickBooks connected.' : (returned.message || 'QuickBooks wasn’t connected.')}
          </span>
        </div>
      )}

      {!status ? (
        <div style={{ fontSize: 14, color: '#888' }}>Checking the connection with QuickBooks...</div>
      ) : (
        <>
          {connected && (
            <div style={{ marginBottom: 12 }}>
              <Detail label="Company">{status.company_name || '—'}</Detail>
              <Detail label="Type">
                {status.environment === 'production'
                  ? <Pill color="#1a7f4b" background="rgba(26, 127, 75, 0.12)">Live company</Pill>
                  : <Pill color="#92400e" background="rgba(217, 119, 6, 0.14)">Sandbox (test) company</Pill>}
              </Detail>
              <Detail label="Connected">{status.connected_at ? formatDateTime(status.connected_at) : '—'}</Detail>
            </div>
          )}

          {state === 'needs_reconnect' && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13, color: '#b91c1c', marginBottom: 12 }}>
              <XCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>The connection to QuickBooks has stopped working. {status.message} Nothing is read from or sent to QuickBooks until it's reconnected.</span>
            </div>
          )}
          {state === 'unreachable' && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13, color: '#92400e', marginBottom: 12 }}>
              <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{status.message}</span>
            </div>
          )}
          {!status.app_ready && !connected && (
            <div style={{ fontSize: 13, color: '#92400e', marginBottom: 12 }}>QuickBooks isn’t set up on this server yet.</div>
          )}

          <div style={{ marginTop: 4 }}>
            {state === 'not_connected' && (
              <button
                type="button"
                onClick={handleConnect}
                disabled={busy || !status.app_ready}
                title="Signs in to QuickBooks to connect a company"
                style={{ ...PRIMARY_BUTTON, opacity: (busy || !status.app_ready) ? 0.6 : 1, cursor: (busy || !status.app_ready) ? 'not-allowed' : 'pointer' }}
              >
                {busy && <Loader2 size={14} strokeWidth={2.5} style={{ animation: 'api-integrations-spin 0.8s linear infinite' }} />}
                Connect to QuickBooks
              </button>
            )}
            {state === 'needs_reconnect' && (
              <button
                type="button"
                onClick={handleConnect}
                disabled={busy}
                title="Signs in to QuickBooks again to restore the connection"
                style={{ ...PRIMARY_BUTTON, opacity: busy ? 0.6 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}
              >
                {busy && <Loader2 size={14} strokeWidth={2.5} style={{ animation: 'api-integrations-spin 0.8s linear infinite' }} />}
                Reconnect
              </button>
            )}
            {connected && (
              <button
                type="button"
                onClick={handleDisconnect}
                disabled={busy}
                title="Disconnects Optim from this QuickBooks company"
                style={{ ...SECONDARY_BUTTON, marginLeft: state === 'needs_reconnect' ? 8 : 0, cursor: busy ? 'not-allowed' : 'pointer' }}
              >
                Disconnect
              </button>
            )}
          </div>
        </>
      )}
    </Accordion>
  );
}

export default QuickBooksCard;
